import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Icon } from '@modulor/react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import Image from '../../Image/Image';
import NoContentPlaceholder from '../../NoContentPlaceholder/NoContentPlaceholder';
import { Local } from '../../Local/Local';
import { ModulorButtonWithAnalytics, VARIANTS } from '../../Modulor/Button';
import { ButtonWithAnalytics } from '../../ui-components/Button/Button';
import { NAMESPACES } from '../../../constants/namespaces';
import { EVENT_ACTION } from '../../../constants/ga';
import { getGameImagePath } from '../../../utils/imagePath';
import {
  gameImageAlternativeSelector,
  gameImageSelector,
} from '../../../redux/reducers/app/configuration';
import styles from './GameLaunchPreviewMobile.module.css';
import { useGoBack } from '../../../hooks/useGoBack';
import BonusNotifications from '../../BonusNotifications/BonusNotifications';
import { useQueryProvider } from '../../../hooks/API/useQueryProviders';
import { isNativeSelector } from '../../../redux/reducers/app/params';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREA_DETAILS,
  ANALYTICS_EVENT_DETAIL,
  ANALYTICS_SCREEN_NAMES,
  ANALYTICS_SCREEN_AREAS,
} from '../../../constants/analytics';
import FEATURES from '../../../constants/featureToggling';

const ANALYTICS_DATA = {
  schema: ANALYTICS_SCHEMA_URLS.GAME_ACTION,
  screen_name: ANALYTICS_SCREEN_NAMES.OPEN_GAME,
  screen_area: ANALYTICS_SCREEN_AREAS.GAME_WINDOW,
  screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.GAME_HEADER,
};

const GameLaunchPreviewMobile = ({
  game,
  isAvailable,
  play,
  playDemo,
  className,
  bonus,
  isGameAvailableForBonusActivity,
  analyticsData,
}) => {
  const [gtmMessage, setGTMMessage] = useState('');
  const gameImagePath = useSelector(gameImageSelector);
  const imageAlternativePath = useSelector(gameImageAlternativeSelector);

  const goBack = useGoBack();
  const { data: provider } = useQueryProvider(game?.provider, {
    enabled: Boolean(game?.provider),
  });
  const isNative = useSelector(isNativeSelector);
  const isExternalNavbarEnabled = useFeatureIsOn(FEATURES.EXTERNAL_NAVBAR);
  const isEnabledDemoMode =
    useFeatureIsOn(FEATURES.ENABLE_DEMO_MODE) &&
    game?.demoModeAvailable &&
    !!playDemo;

  return (
    <div className={classNames(styles.component, className)}>
      {!isNative && !isExternalNavbarEnabled && (
        <div className={styles['lc-header']}>
          <ButtonWithAnalytics
            className={styles.close}
            onClick={() => goBack()}
            text={<Icon name="back_ios" className={styles.icon} />}
            analyticsData={{
              ...ANALYTICS_DATA,
              event_detail: ANALYTICS_EVENT_DETAIL.CLOSE_GAME,
              game_id: game?.id,
              provider_id: game?.provider,
            }}
          />
        </div>
      )}

      {isAvailable ? (
        <div className={styles['lc-container']}>
          <div className={styles['lc-center']}>
            <Image
              src={getGameImagePath(gameImagePath, game?.id, false)}
              srcAlternative={getGameImagePath(imageAlternativePath, game?.id)}
              className={styles.image}
            />
            <h1 className={styles.title}>
              <Local namespace={NAMESPACES.GAME}>{game?.translationKey}</Local>
            </h1>
            <div className={styles.provider}>
              {provider?.translationKey ?? '...'}
            </div>
          </div>

          {isGameAvailableForBonusActivity && (
            <BonusNotifications
              bonus={bonus}
              isDesktop={false}
              setGTMMessage={setGTMMessage}
            />
          )}

          <ModulorButtonWithAnalytics
            onClick={play}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.GAME_PREVIEW_PAGE_REAL,
              game_id: game?.id,
              provider_id: game?.provider,
              message: isGameAvailableForBonusActivity ? gtmMessage : '',
            }}
            analyticsData={{
              ...analyticsData,
              schema: ANALYTICS_SCHEMA_URLS.PLAY_GAME_CLICK,
              screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.PREVIEW_ACTIONS,
              game_id: game?.id,
              provider_id: game?.provider,
            }}
            className={styles.play}
          >
            <Local>Play</Local>
          </ModulorButtonWithAnalytics>
          {isEnabledDemoMode && (
            <ModulorButtonWithAnalytics
              onClick={playDemo}
              dataLayerEventData={{
                eventAction: EVENT_ACTION.GAME_PREVIEW_PAGE_DEMO,
                game_id: game?.id,
                provider_id: game?.provider,
                message: isGameAvailableForBonusActivity ? gtmMessage : '',
              }}
              analyticsData={{
                ...analyticsData,
                schema: ANALYTICS_SCHEMA_URLS.PLAY_DEMO_GAME_CLICK,
                screen_area_detail:
                  ANALYTICS_SCREEN_AREA_DETAILS.PREVIEW_ACTIONS,
                game_id: game?.id,
                provider_id: game?.provider,
              }}
              className={styles.demo}
              variant={VARIANTS.SECONDARY}
            >
              <Local>Demo</Local>
            </ModulorButtonWithAnalytics>
          )}
        </div>
      ) : (
        <div className={styles['lc-container']}>
          <NoContentPlaceholder
            className={styles.empty}
            title="The game is temporarily unavailable, but will be back soon"
          />
        </div>
      )}
    </div>
  );
};

GameLaunchPreviewMobile.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    status: PropTypes.string,
    category: PropTypes.array,
    provider: PropTypes.string,
    translationKey: PropTypes.string,
    demoModeAvailable: PropTypes.bool,
  }),
  play: PropTypes.func,
  playDemo: PropTypes.func,
  isLoading: PropTypes.bool,
  isAvailable: PropTypes.bool,
  className: PropTypes.string,
  isGameAvailableForBonusActivity: PropTypes.bool,
  bonus: PropTypes.shape(),
};

GameLaunchPreviewMobile.defaultProps = {
  game: {},
  isLoading: true,
  isAvailable: true,
  isGameAvailableForBonusActivity: false,
};

export default GameLaunchPreviewMobile;
